/**
 * Плитки выбора кабинетов маркетплейсов и сайта — общий компонент мастеров
 * «Создать макеты» (цели полного пайплайна) и «Создать пряники» (визуалы и карточки).
 *
 * Компонент без своего состояния: список кабинетов и выбор держит вызывающий (`useSellerTargets`).
 * Ключ кабинета в выборе — `sellerTargetKey` (`площадка:id продавца`).
 */
import { Loader2, RefreshCw } from 'lucide-react'
import { Button } from './ui'
import { sellerTargetKey, type SellerLoadState, type SellerTarget } from './useSellerTargets'

/**
 * Стиль карточки кабинета на шаге выбора целей. Ключ — код площадки реестра
 * плюс `site` (кабинет сайта `site_pt`). `Record`, а не тернарник `wb ? A : B`:
 * новая площадка получает свой стиль, а не молча становится синей карточкой OZON.
 */
export const SELLER_TARGET_STYLE: Record<'wb' | 'ozon' | 'ym' | 'site', {
  selectedClass: string
  accentClass: string
  badgeClass: string
  label: string
}> = {
  wb: {
    selectedClass: 'border-fuchsia-400 bg-fuchsia-50 ring-1 ring-fuchsia-200 dark:border-fuchsia-600 dark:bg-fuchsia-950/30 dark:ring-fuchsia-900',
    accentClass: 'accent-fuchsia-600',
    badgeClass: 'bg-fuchsia-600',
    label: 'wb',
  },
  ozon: {
    selectedClass: 'border-blue-400 bg-blue-50 ring-1 ring-blue-200 dark:border-blue-600 dark:bg-blue-950/30 dark:ring-blue-900',
    accentClass: 'accent-blue-600',
    badgeClass: 'bg-blue-600',
    label: 'ozon',
  },
  ym: {
    selectedClass: 'border-amber-400 bg-amber-50 ring-1 ring-amber-200 dark:border-amber-600 dark:bg-amber-950/30 dark:ring-amber-900',
    accentClass: 'accent-amber-600',
    badgeClass: 'bg-amber-600',
    label: 'ym',
  },
  site: {
    selectedClass: 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200 dark:border-emerald-600 dark:bg-emerald-950/30 dark:ring-emerald-900',
    accentClass: 'accent-emerald-600',
    badgeClass: 'bg-emerald-600',
    label: 'сайт',
  },
}

export interface SellerTargetTilesProps {
  targets: SellerTarget[]
  loadState: SellerLoadState
  loadError: string
  unsupportedCount: number
  /** Ключи выбранных кабинетов (`sellerTargetKey`). */
  selected: string[]
  onToggle: (key: string, checked: boolean) => void
  onRetry: () => void
  /** Хвост текста «кабинеты не найдены / не загрузились», например «Полный пайплайн требует хотя бы один кабинет.». */
  emptyHint?: string
  /** Подсказка под плитками, когда кабинеты загружены, но ни один не выбран. */
  emptySelectionHint?: string
}

export function SellerTargetTiles({
  targets,
  loadState,
  loadError,
  unsupportedCount,
  selected,
  onToggle,
  onRetry,
  emptyHint,
  emptySelectionHint,
}: SellerTargetTilesProps) {
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {(loadState === 'idle' || loadState === 'loading') && (
          <p className="col-span-full flex min-h-11 items-center gap-2 rounded-xl border border-gray-200 px-3 text-xs text-gray-500 dark:border-zinc-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            Загружаем кабинеты…
          </p>
        )}
        {(loadState === 'empty' || loadState === 'error') && (
          <div className="col-span-full rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
            <p>
              {loadState === 'error'
                ? loadError
                : 'Активные кабинеты поддерживаемых маркетплейсов не найдены.'}
              {emptyHint ? ` ${emptyHint}` : ''}
            </p>
            <Button variant="secondary" size="sm" onClick={onRetry} className="mt-2 min-h-11">
              <RefreshCw className="mr-2 h-4 w-4" />
              Повторить
            </Button>
          </div>
        )}
        {unsupportedCount > 0 && (
          <p className="col-span-full text-xs text-amber-700 dark:text-amber-300">
            Пропущено кабинетов с неизвестным маркетплейсом: {unsupportedCount}.
          </p>
        )}
        {targets.map((target) => {
          const key = sellerTargetKey(target)
          const isSelected = selected.includes(key)
          const style = SELLER_TARGET_STYLE[target.marketplace as keyof typeof SELLER_TARGET_STYLE]
            ?? SELLER_TARGET_STYLE.ozon
          return (
            <label
              key={key}
              className={`relative flex min-h-20 cursor-pointer items-center gap-3 rounded-xl border p-3 pr-10 text-sm shadow-sm transition-colors ${
                isSelected
                  ? style.selectedClass
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600 dark:hover:bg-zinc-800'
              }`}
            >
              <input
                type="checkbox"
                aria-label={`${style.label.toUpperCase()}, ${target.name}, кабинет ${target.cabinet}`}
                checked={isSelected}
                onChange={(event) => onToggle(key, event.target.checked)}
                className={`absolute right-3 top-3 h-4 w-4 ${style.accentClass}`}
              />
              <span className={`shrink-0 rounded-md px-2.5 py-1 text-2xs font-bold uppercase text-white shadow-sm ${style.badgeClass}`}>
                {style.label}
              </span>
              <span className="min-w-0 pr-1">
                <span className="block truncate font-medium">{target.name}</span>
                <span className="block truncate text-xs text-gray-500">Кабинет: {target.cabinet}</span>
              </span>
            </label>
          )
        })}
      </div>
      {loadState === 'ready' && selected.length === 0 && emptySelectionHint && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{emptySelectionHint}</p>
      )}
    </>
  )
}
